/**
 * Group Company Screen — Custom form matching PDF
 * 3-column layout: Company details | Auth details | Config
 * Table: HBScompanies
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { Save, Grid3X3, XCircle, Plus } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const enterNav = (e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); const f = e.target.closest('form'); if (!f) return; const els = Array.from(f.querySelectorAll('input:not([disabled]):not([readonly]),select:not([disabled])')); const i = els.indexOf(e.target); if (i >= 0 && i < els.length - 1) els[i + 1].focus(); } };
const F = memo(({ label, value, onChange, required, disabled, type = 'text' }) => (<div className="erp-form-row"><label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label><div className="erp-form-control"><input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="erp-input" onKeyDown={enterNav} /></div></div>));

const GroupCompanyScreen = ({ theme = 'light', onBack }) => {
  const [view, setView] = useState('grid');
  const [form, setForm] = useState({ Gcompid: '', GCompInit: '', Gcompany: '', Noofcompanies: '1', Gusername: '', Gpassword: '', DBName: '', DBpwd: '', Noofdays: '365' });
  const [data, setData] = useState([]);
  const [mode, setMode] = useState('add');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dynValues, setDynValues] = useState({});
  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';
  const isView = mode === 'view';
  const set = (n, v) => setForm(p => ({ ...p, [n]: v }));

  const loadData = useCallback(async () => {
    if (!token) return;
    try { const r = await fetch(`${API_URL}/api/foundation/groupcompany`, { headers: { Authorization: `Bearer ${token}` } }); const j = await r.json(); if (j.success) setData(j.data || []); } catch {}
  }, [token]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!form.Gcompany?.trim()) { setError('GCompany Name is required'); return; }
    setSaving(true); setError('');
    try {
      erpToast.success(mode === 'edit' ? 'Updated!' : 'Saved!');
      loadData();
      setForm({ Gcompid: '', GCompInit: '', Gcompany: '', Noofcompanies: '1', Gusername: '', Gpassword: '', DBName: '', DBpwd: '', Noofdays: '365' });
      setMode('add');
    } catch { setError('Save failed'); }
    setSaving(false);
  };

  if (view === 'grid') {
    const GC_COLUMNS = [
      { field: 'Gcompid', header: 'GCompId' },
      { field: 'GCompInit', header: 'GCompInit' },
      { field: 'Gcompany', header: 'GCompany' },
      { field: 'Noofcompanies', header: 'NoOfCompanies' },
      { field: 'Gusername', header: 'GUserName' },
    ];
    return (
      <div className="erp-module">
        <ERPAdvancedGrid
          title="Group Company"
          columns={GC_COLUMNS}
          data={data}
          total={data.length}
          idField="Gcompid"
          nameField="Gcompany"
          page={1}
          pageSize={data.length || 10}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          loading={false}
          onAddNew={() => { setView('form'); setMode('add'); setForm({ Gcompid: '', GCompInit: '', Gcompany: '', Noofcompanies: '1', Gusername: '', Gpassword: '', DBName: '', DBpwd: '', Noofdays: '365' }); }}
          onEdit={(row, m) => { setForm({ ...row }); setMode(m || 'edit'); setView('form'); }}
          testId="group-company"
          exportFileName="GroupCompany"
          showFilters={false}
        />
      </div>
    );
  }

  return (
    <div className="erp-module">
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Group Company</span></button>) : (<span className="erp-screen-title">Group Company</span>)}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11 }}>
          {form.Gcompid ? <span>ID: <strong style={{ fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>{form.Gcompid}</strong></span>
          : <><span style={{ color: 'var(--erp-text-muted)' }}>Previous ID: <strong style={{ fontFamily: 'var(--erp-font-mono)' }}>{data[0]?.Gcompid || '—'}</strong></span><span style={{ color: 'var(--erp-text-secondary)' }}>New ID: <strong style={{ color: 'var(--erp-warning)', fontFamily: 'var(--erp-font-mono)' }}>Auto-generated</strong></span></>}
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}><Save size={13} /> {saving ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={() => setView('grid')}><Grid3X3 size={13} /> Gridview</button>
          <button className="erp-btn erp-btn-ghost" onClick={() => { setForm({ Gcompid: '', GCompInit: '', Gcompany: '', Noofcompanies: '1', Gusername: '', Gpassword: '', DBName: '', DBpwd: '', Noofdays: '365' }); setMode('add'); setError(''); setDynValues({}); }}><XCircle size={13} /> Clear</button>
          {isSuperAdmin && !isView && <button className="erp-btn erp-btn-primary" onClick={() => document.querySelector('[data-testid="add-dynamic-field-btn"]')?.click()}><Plus size={13} /> Add Field</button>}
        </div>
      </div>
      {error && <div className="erp-alert-error">{error}</div>}
      <form style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }} onSubmit={(e) => e.preventDefault()}>
        <div className="erp-form-card"><div className="erp-form-section">
          <div className="erp-form-3col">
            <div>
              <F label="GCompany ID" value={form.Gcompid} onChange={(v) => set('Gcompid', v)} required disabled />
              <F label="GCompany Initial" value={form.GCompInit} onChange={(v) => set('GCompInit', v)} required disabled={isView} />
              <F label="GCompany Name" value={form.Gcompany} onChange={(v) => set('Gcompany', v)} required disabled={isView} />
              <F label="No.Of Companies" value={form.Noofcompanies} onChange={(v) => set('Noofcompanies', v)} type="number" disabled={isView} />
              <F label="No.Of Days" value={form.Noofdays} onChange={(v) => set('Noofdays', v)} required type="number" disabled={isView} />
            </div>
            <div>
              <F label="GUser Name" value={form.Gusername} onChange={(v) => set('Gusername', v)} disabled={isView} />
              <F label="GPassword" value={form.Gpassword} onChange={(v) => set('Gpassword', v)} type="password" disabled={isView} />
              <F label="DataBase Name" value={form.DBName} onChange={(v) => set('DBName', v)} required disabled={isView} />
              <F label="Db Password" value={form.DBpwd} onChange={(v) => set('DBpwd', v)} required type="password" disabled={isView} />
            </div>
            <div>
              <F label="Prefix" value={form.GCompInit} onChange={() => {}} disabled />
              <F label="Creation Date" value={new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} onChange={() => {}} disabled />
            </div>
          </div>
        </div></div>
        <DynamicFieldRenderer screenName="GroupCompany" recordId={form.Gcompid} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
        <div className="erp-embedded-grid"><div className="erp-grid-wrapper">
          <table className="erp-grid erp-grid-header-fixed"><thead><tr><th style={{ width: 50 }}>Actions</th><th>GCompId</th><th>GCompInit</th><th>GCompany</th><th>NoOfCompanies</th><th>GUserName</th></tr></thead></table>
          <div className="erp-grid-body-scroll"><table className="erp-grid"><tbody>{data.map(row => (
            <tr key={row.Gcompid}><td style={{ width: 50 }}><ERPActionColumn row={{ ...row, CatId: row.Gcompid }} onView={() => { setForm({ ...row }); setMode('view'); }} onEdit={() => { setForm({ ...row }); setMode('edit'); }} /></td>
            <td className="cell-mono">{row.Gcompid}</td><td>{row.GCompInit}</td><td className="cell-bold">{row.Gcompany}</td><td>{row.Noofcompanies}</td><td>{row.Gusername}</td></tr>
          ))}</tbody></table></div>
        </div></div>
      </form>
    </div>
  );
};

export default GroupCompanyScreen;
